/* istanbul ignore file */ // not fully implemented yet
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Divider, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import {
  ScenarioContext,
} from '@bingads-webui-react/hoc-with-signal';
import { getImageDetailAsync } from '@bingads-webui-campaign/image-experience-utils';
import { useImagePicker } from '@bingads-webui-campaign-react/add-image-hooks';
import { MediaRepository } from '@bingads-webui-campaign/media-repository';
import { MediaEditorPanel } from '@bingads-webui-campaign-react/media-editor-panel';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { RecommendedImage } from '@bingads-webui-campaign-react/display-recommended-image';
import { CSMLAssetSource } from '@bingads-webui-campaign-react/asset-library-model';
import { AdsStudioDisplayColorPicker } from './background-asset';
import { UploadImage } from './upload-image';
import { ActionPaneProps } from './type';
import { imageAspectRatioConfig } from '../utils';


const useStyles = makeStyles({
  divider: {
    ...shorthands.margin(tokens.spacingVerticalL, 0),
  },
});

type payloadType = {url: string, cropSetting: {[key: string]: any}}
// TODO: DATA WILL CHANGE ONCE WE HAVE DATA COMING FROM BACKEND
const ActionPane: React.FC<ActionPaneProps> = ({
  i18n,
  permissions,
  cid,
  aid,
  oDataConfig,
  isAssetsLoading,
  serverContext = {},
  preferenceService,
  perfMarker,
  imagesUrl,
  logosUrl,
  images,
  logos,
  actionPanelDisplayData,
  onImageChange,
  onLogoChange,
  backgroundColors,
  onBackgroundColorChange,
  recommendedAssets,
  showRecommendationsBanner,
}) => {
  const scenario = useContext(ScenarioContext);
  const [editPanel, setEditPanel] = useState(false);
  const [assetType, setAssetType] = useState('');
  const [internalEffectSettings, setEffectSettings] = useState(null);
  const classes = useStyles();
  // const [internalCropSetting, setCropSetting] = useState(null);
  const {
    displayImagePicker,
    setDisplayImagePicker,
    selectedMediaPanelSource,
    setSelectedMediaPanelSource,
  } = useImagePicker();

  const imageAssets = recommendedAssets?.imageAssets || [];
  const logoAssets = recommendedAssets?.logo || [];
  const themesImages = recommendedAssets?.themesImages || [];
  const themesLogos = recommendedAssets?.themesLogos || [];
  const maxColorsLimit = (recommendedAssets?.color?.length || 0) + 1;
  const selectedColor = useMemo(() => {
    if (backgroundColors && backgroundColors.size === 1) {
      return backgroundColors.values().next().value;
    }
    return '';
  }, [backgroundColors]);

  // show brandkit and display ads selected color
  const paletteData = useMemo(() => {
    const data = Array.from(recommendedAssets?.color || []);
    backgroundColors.forEach((key) => {
      if (!data.some(color => color.HexCode === key) && key) {
        data.push({ HexCode: key, Name: key });
      }
    });
    return data;
  }, [recommendedAssets?.color, backgroundColors]);
  // TODO: CHANGE THIS TO USE DATA COMING FROM BACKEND

  const mediaRepository = useMemo(() => new MediaRepository({
    cid, aid, oDataConfig,
  }), [aid, cid, oDataConfig]);

  const onDeleteColor = useCallback((ev) => {
    const { colorid } = ev.currentTarget.dataset;
    let data = Array.from(recommendedAssets?.color || []);
    data = data.filter(color => color.Name !== colorid);
    if (data.length > 0) {
      onBackgroundColorChange((data[0])?.HexCode);
    }
  }, [onBackgroundColorChange, recommendedAssets?.color]);

  const handleAssetChange = useCallback((payload, type = '', keepCropSettings = false) => {
    if (type === 'logo' || assetType === 'logo') {
      onLogoChange(payload);
    } else {
      onImageChange(payload, keepCropSettings);
    }
  }, [assetType, onImageChange, onLogoChange]);


  const onReplacePreview = useCallback((ev) => {
    const { type } = ev.currentTarget.dataset;
    setDisplayImagePicker(true);
    setAssetType(type);
  }, [setDisplayImagePicker]);

  const onDismissEditPanel = useCallback(() => {
    setEditPanel(false);
    setAssetType('');
  }, []);


  const onSelectRecommendedImage = (data, type) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { fullUrl, index } = data;
    getImageDetailAsync(fullUrl).then(({ naturalWidth, naturalHeight }) => {
      let assetData = imageAssets[index];
      if (type === 'logo') {
        assetData = logoAssets[index];
      }
      const payload = {
        url: fullUrl,
        assetId: assetData.assetId,
        imageSize: {
          width: naturalWidth,
          height: naturalHeight,
        },
      };
      if (assetData.isBrandKitImage && assetData.cropSetting) {
        (payload as any).cropSetting = assetData.cropSetting;
      }
      handleAssetChange(payload, type);
    });
  };
  const onAddImages = (imageData, type) => {
    const image = imageData[0];
    const payload = {
      url: image?.src || image?.image?.src,
      imageSize: {
        width: image?.width || image?.image?.width,
        height: image?.height || image?.image?.height,
      },
      assetId: image?.id || image?.image?.id,
    };
    handleAssetChange(payload, type);
  };
  const onBackgroundChange = (color, colorObj) => {
    onBackgroundColorChange(colorObj.str);
  };

  const imageUrl = useMemo(() => {
    if (imagesUrl && imagesUrl.size === 1) {
      return imagesUrl.values().next().value;
    }
    return '';
  }, [imagesUrl]);

  const logoUrl = useMemo(() => {
    if (logosUrl && logosUrl.size === 1) {
      return logosUrl.values().next().value;
    }
    return '';
  }, [logosUrl]);

  const imageAspectRatio = useMemo(() => {
    if (actionPanelDisplayData.imageAspectRatio.size === 1) {
      return actionPanelDisplayData.imageAspectRatio.values().next().value;
    }
    return '';
  }, [actionPanelDisplayData]);

  const logoAspectRatio = useMemo(() => {
    if (actionPanelDisplayData && actionPanelDisplayData.logoAspectRatio.size === 1) {
      return actionPanelDisplayData.logoAspectRatio.values().next().value;
    }
    return '';
  }, [actionPanelDisplayData]);

  const baseImage = useMemo(() => {
    let data = images?.[0];
    let aspectRatio = imageAspectRatio;
    if (assetType === 'logo') {
      aspectRatio = logoAspectRatio;
      data = logos?.[0];
    }
    const cropSetting = data?.cropSetting?.[0];
    return {
      Src: data?.url,
      AltText: i18n.getString(_TL_('template image')),
      Height: data?.imageSize?.height,
      Type: imageAspectRatioConfig[aspectRatio]?.type,
      Width: data?.imageSize?.width,
      CropSetting: {
        SourceWidth: cropSetting?.sourceWidth ?? cropSetting?.SourceWidth ?? data?.imageSize?.width,
        SourceHeight: cropSetting?.sourceHeight ?? cropSetting?.SourceHeight ?? data?.imageSize?.height,
        SourceX: cropSetting?.sourceX ?? cropSetting?.SourceX ?? 0,
        SourceY: cropSetting?.sourceY ?? cropSetting?.SourceY ?? 0,
      },
    };
  }, [assetType, imageAspectRatio, images, logoAspectRatio, logos]);
  const onEditPreview = useCallback((ev) => {
    const { type } = ev.currentTarget.dataset;
    let data = [...images];
    if (type === 'logo') {
      data = [...logos];
    }
    if (data?.[0]?.imageSize?.width && data?.[0]?.imageSize?.height) {
      setAssetType(type);
      setEditPanel(true);
      return;
    }
    getImageDetailAsync(data?.[0]?.url).then(({ naturalWidth, naturalHeight }) => {
      data[0].imageSize = { width: naturalWidth, height: naturalHeight };
      setAssetType(type);
      setEditPanel(true);
    });
  }, [images, logos]);

  const hideEditPreviewBtnImage = !logoAspectRatio;
  const hideCropImage = !imageAspectRatio;
  const minCropSize = assetType === 'logo' ? imageAspectRatioConfig[logoAspectRatio]?.minCropSize : imageAspectRatioConfig[imageAspectRatio]?.minCropSize;
  const isMultpleImages = imagesUrl && imagesUrl.size > 1;
  const isMultpleLogos = logosUrl && logosUrl.size > 1;
  const onSaveEditImage = useCallback((effectSettings) => {
    setEffectSettings(effectSettings);
    // setCropSetting(cropSettings);
    setEditPanel(false);
    setAssetType('');
    const payload: payloadType | {} = {};
    let keepCropSettings = false;
    if (effectSettings?.effectSettings?.effectSrc) {
      (payload as payloadType).url = effectSettings.effectSettings?.effectSrc;
      keepCropSettings = true;
    }
    if (effectSettings?.cropSettings && (assetType === 'logo' || !hideCropImage)) {
      (payload as payloadType).cropSetting = [effectSettings.cropSettings];
    }
    if (Object.keys(payload).length > 0) {
      handleAssetChange(payload, '', keepCropSettings);
    }
  }, [assetType, handleAssetChange, hideCropImage, images, logos]);

  return (
    <div className="action-pane">
      {(actionPanelDisplayData.displayLogo && showRecommendationsBanner) &&
      <Divider appearance="strong" className={classes.divider} />}
      {actionPanelDisplayData.displayLogo && <RecommendedImage
        i18n={i18n}
        maxUploadNum={1}
        previewImage={logoUrl}
        hideEditPreviewBtn={hideEditPreviewBtnImage}
        isMultiplePreview={isMultpleLogos}
        recommendedImages={logoAssets || []}
        isLoading={isAssetsLoading}
        uiBlocking={false}
        type="logo"
        helpTopic="pop_BA_DisplayTemplate_Logo"
        helpLabel={i18n.getString(_TL_('Learn more about logos'))}
        themes={themesLogos || []}
        seeAllPanelTitle={_TL_('Recommended logos')}
        onEditPreviewImage={onEditPreview}
        onReplacePreviewImage={onReplacePreview}
        recommendedLoadingText={i18n.getString(_TL_('Loading'))}
        title={i18n.getString(_TL_('Logo'))}
        recommendationTitle={i18n.getString(_TL_('Recommended logos'))}
        onSelectRecommendedImage={onSelectRecommendedImage}
        seeAllLinkText={_TL_('View all')}
        showViewAllInHeader
      />}
      {editPanel && <MediaEditorPanel
        isOpen={editPanel}
        onDismiss={onDismissEditPanel}
        onApply={onSaveEditImage}
        disableCrop={hideCropImage}
        isLogo={assetType === 'logo'}
        permissions={permissions}
        hasRightRail={false}
        effectSettings={internalEffectSettings}
        cropSettings={baseImage.CropSetting}
        // onManualCropImage={onManualCropImage}
        // onManualCropLogo={onManualCropLogo}
        minCropSize={minCropSize}
        baseImage={baseImage}
        mediaRepository={mediaRepository}
        adCreatorScenario={{
          scenarioName: `${(scenario as any).name}AdCreation`,
          scenarioValue: `${(scenario as any).name}AdCreation`,
        }}
      />}
      {displayImagePicker && (
        <UploadImage
          displayImagePicker={displayImagePicker}
          setDisplayImagePicker={setDisplayImagePicker}
          selectedMediaPanelSource={selectedMediaPanelSource}
          setSelectedMediaPanelSource={setSelectedMediaPanelSource}
          permissions={permissions}
          i18n={i18n}
          maxLimit={1}
          preferenceService={preferenceService}
          serverContext={serverContext}
          aid={aid}
          cid={cid}
          onSelectImages={onAddImages}
          oDataConfig={oDataConfig}
          perfMarker={perfMarker}
          elementType={assetType}
          disabledAssetSources={[CSMLAssetSource.Shutterstock]}
        />
      )}
      {(actionPanelDisplayData.displayImage && (showRecommendationsBanner || actionPanelDisplayData.displayLogo)) &&
      <Divider appearance="strong" className={classes.divider} />}
      {actionPanelDisplayData.displayImage && <RecommendedImage
        i18n={i18n}
        maxUploadNum={1}
        previewImage={imageUrl}
        helpTopic="pop_BA_DisplayTemplate_Image"
        helpLabel={i18n.getString(_TL_('Learn more about images'))}
        recommendedImages={imageAssets || []}
        isLoading={isAssetsLoading}
        uiBlocking={false}
        type="image"
        isMultiplePreview={isMultpleImages}
        themes={themesImages || []}
        seeAllPanelTitle={_TL_('Recommended images')}
        onEditPreviewImage={onEditPreview}
        onReplacePreviewImage={onReplacePreview}
        recommendedLoadingText={i18n.getString(_TL_('Loading'))}
        title={i18n.getString(_TL_('Image'))}
        recommendationTitle={_TL_('Recommended images')}
        onSelectRecommendedImage={onSelectRecommendedImage}
        seeAllLinkText={_TL_('View all')}
        showViewAllInHeader
      />}
      { actionPanelDisplayData.displayBackground && <Divider appearance="strong" className={classes.divider} />}
      {actionPanelDisplayData.displayBackground &&
      <AdsStudioDisplayColorPicker
        i18n={i18n}
        maxColors={maxColorsLimit}
        helpTopic="pop_BA_DisplayAds_Color"
        helpLabel="Learn more about background colors"
        onDelete={onDeleteColor}
        paletteData={paletteData}
        isLoading={isAssetsLoading}
        color={selectedColor}
        onUpdate={onBackgroundChange}
        title={i18n.getString(_TL_('Background color'))}
      />
      }
    </div>
  );
};

export const ActionPaneComp = withInstrumentation(ActionPane);
